/**
 * Best-effort Screen Wake Lock helper.
 * Keeps the display awake during active capture; browsers release the lock when
 * the tab is hidden, so callers should re-request after resume / visibility return.
 */
export class ScreenWakeLockService {
  constructor() {
    this.wakeLock = null;
    this.desired = false;
    this.releasing = false;
  }

  isSupported() {
    return typeof navigator !== 'undefined' && !!navigator.wakeLock?.request;
  }

  /**
   * Mark wake lock as desired and request if the page is visible.
   * @returns {Promise<boolean>}
   */
  async acquire() {
    this.desired = true;
    return this.requestIfPossible();
  }

  /**
   * Release the lock and clear the desired flag (capture no longer needs it).
   */
  async release() {
    this.desired = false;
    await this.releaseHeld();
  }

  /**
   * Re-request only if capture still wants the lock (e.g. after tab becomes visible).
   */
  async reacquireIfDesired() {
    if (!this.desired) return false;
    return this.requestIfPossible();
  }

  async requestIfPossible() {
    if (!this.desired || !this.isSupported()) return false;
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
      return false;
    }

    try {
      await this.releaseHeld();
      this.wakeLock = await navigator.wakeLock.request('screen');
      this.wakeLock.addEventListener('release', () => {
        this.wakeLock = null;
        if (this.releasing || !this.desired) return;
        if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
          this.requestIfPossible();
        }
      });
      return true;
    } catch (error) {
      // NotAllowedError / unsupported policies — non-fatal; capture continues
      // until the track is muted/ended or AudioContext is interrupted
      console.warn('Screen wake lock unavailable:', error);
      this.wakeLock = null;
      return false;
    }
  }

  async releaseHeld() {
    if (!this.wakeLock) return;
    this.releasing = true;
    try {
      await this.wakeLock.release();
    } catch {
      // ignore
    }
    this.wakeLock = null;
    this.releasing = false;
  }
}

export default ScreenWakeLockService;
